<template>
  <div class="page-content-wrapper">
    <div class="card rounded-3 shadow border-1">
      <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center mb-0">
        <p class="text-s fw-bold mb-0">Select your services</p>
        <!-- Search Bar -->
        <SearchBar
          v-model="searchQuery"
          placeholder="Search services..."
          label="Search"
        />
      </div>
      <div class="card-body">
        <CustomTabs :tabs="filteredTabs" v-model="currentTab"/>

        <!-- Loading State -->
        <div v-if="isLoading || (currentServices.length === 0 && !searchQuery)" class="loading-container">
          <ServiceCardSkeleton :count="6" />
        </div>
        <!-- Magnetic Carousel -->
        <div class="carousel-section" v-else>
          <div v-if="filteredServices.length === 0" class="no-results-message">
            <i class="fas fa-search"></i>
            <p>No services found matching "{{ searchQuery }}"</p>
          </div>
          <MagneticCarousel
            v-else
            ref="magneticCarousel"
            :cards="filteredServices"
            @toggle-service="handleToggleService"
            @show-details="handleShowDetails"
            @card-changed="handleCardChanged"
          />
        </div>
        <!-- Selected Services Summary -->
        <div
          class="selected-services-container"
          v-if="allSelectedServices.length > 0"
        >
          <div class="selected-services-card">
            <div class="selected-header">
              <div class="selected-title-section">
                <h5>Selected Services</h5>
                <span class="badge">{{ allSelectedServices.length }}</span>
              </div>
            </div>

            <div class="selected-services-grid">
              <div
                v-for="service in allSelectedServices"
                :key="service.id"
                class="selected-service-chip"
                @click="navigateToService(service)"
              >
                <span class="chip-name">{{
                  service.service_name || service.name
                }}</span>
                <button
                  class="chip-remove"
                  @click.stop="removeService(service)"
                  aria-label="Remove service"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div class="selected-actions">
              <button
                class="btn-primary btn-sm bg-green"
                @click="proceedToNext"
              >
                Continue
                <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
        <!-- Empty State -->
        <div
          class="selected-services-container"
          v-else
        >
          <div class="empty-state-card">
            <i class="fas fa-hand-pointer empty-icon"></i>
            <h5>No Services Selected</h5>
            <p>Select the above services you'd like</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Description Modal -->
    <div
      class="modal fade"
      id="service_description_modal"
      tabindex="-1"
      aria-labelledby="service_description_label"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-s" id="service_description_label"></h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body text-xs">
            <p><span id="service_description"></span></p>
            <p><strong>This service returns information such as:</strong></p>
            <ul id="service_output"></ul>
            <p><strong>Required Inputs:</strong></p>
            <ul id="service_input"></ul>
            <p><strong>Cost Per Request:</strong></p>
            <ul>
              <li>R <span id="service_cost"></span></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomTabs from "@/components/CustomTab/CustomTab.vue";
import MagneticCarousel from "@/components/CustomCard/MagneticCarousel.vue";
import ServiceCardSkeleton from "@/components/SkeletonLoader/ServiceCardSkeleton.vue";
import SearchBar from "@/components/SearchBar/SearchBar.vue";
import { useToast } from "@/components/Toast/useToast";
import { API_BASE_URL } from "@/config";

export default {
  name: "SelectServices",
  components: {
    CustomTabs,
    MagneticCarousel,
    ServiceCardSkeleton,
    SearchBar,
  },
  props: {
    personnelServices: {
      type: Array,
      default: () => [],
    },
    companyServices: {
      type: Array,
      default: () => [],
    },
    complianceServices: {
      type: Array,
      default: () => [],
    },
    creditServices: {
      type: Array,
      default: () => [],
    },
    customForms: {
      type: Array,
      default: () => [],
    },
    includeCustomForms: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["continue", "toggle-service"],
  setup() {
    const { showWarning } = useToast();
    return { showWarning };
  },
  data: () => ({
    currentTab: localStorage.getItem("select_services_tab") || "personnel",
    searchQuery: "",
    allTabs: [
      { label: "Person Verification", value: "personnel" },
      { label: "Company Verification", value: "company" },
      { label: "Compliance Verification", value: "compliance" },
      { label: "Credit Verification", value: "credit" },
      { label: "Custom Forms", value: "custom" },
    ],
  }),
  computed: {
    filteredTabs() {
      if (this.includeCustomForms) {
        return this.allTabs;
      }
      return this.allTabs.filter((tab) => tab.value !== "custom");
    },
    currentServices() {
      if (this.searchQuery.trim()) {
        return [
          ...this.personnelServices,
          ...this.companyServices,
          ...this.complianceServices,
          ...this.creditServices,
          ...(this.includeCustomForms ? this.customForms : []),
        ].map((s) => this.transformService(s));
      }

      switch (this.currentTab) {
        case "personnel":
          return this.personnelServices.map((s) => this.transformService(s));
        case "company":
          return this.companyServices.map((s) => this.transformService(s));
        case "compliance":
          return this.complianceServices.map((s) => this.transformService(s));
        case "credit":
          return this.creditServices.map((s) => this.transformService(s));
        case "custom":
          return this.customForms.map((s) => this.transformService(s));
        default:
          return [];
      }
    },
    allSelectedServices() {
      return [
        ...this.personnelServices.filter((s) => s.is_selected),
        ...this.companyServices.filter((s) => s.is_selected),
        ...this.complianceServices.filter((s) => s.is_selected),
        ...this.creditServices.filter((s) => s.is_selected),
        ...(this.includeCustomForms
          ? this.customForms.filter((s) => s.is_selected)
          : []),
      ];
    },
    filteredServices() {
      if (!this.searchQuery.trim()) {
        return this.currentServices;
      }

      const query = this.searchQuery.toLowerCase();
      return this.currentServices.filter((service) => {
        const name = (service.service_name || service.name || "").toLowerCase();
        const description = (
          service.description ||
          this.getServiceDescription(service) ||
          ""
        ).toLowerCase();
        const category = (
          service.category ||
          this.getServiceCategory(service) ||
          ""
        ).toLowerCase();

        return (
          name.includes(query) ||
          description.includes(query) ||
          category.includes(query)
        );
      });
    },
  },
  watch: {
    currentTab(newTab) {
      localStorage.setItem("select_services_tab", newTab);
    },
  },
  methods: {
    transformService(service) {
      return {
        ...service,
        name: service.service_name || service.name,
        category: this.getServiceCategory(service),
        description: this.getServiceDescription(service),
      };
    },
    getServiceCategory(service) {
      if (service.service_details && service.service_details.length > 0) {
        const categoryName =
          service.service_details[0].service_categories?.category_name;
        if (categoryName) {
          return categoryName
            .replace("_", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
        }
      }
      return service.category || "Service";
    },
    getServiceDescription(service) {
      if (service.service_details && service.service_details.length > 0) {
        return service.service_details[0].service_description || "";
      }
      return "Custom service";
    },

    handleToggleService(service, checked) {
      this.$emit("toggle-service", service, checked);
    },

    clearSearch() {
      this.searchQuery = "";
    },
    handleCardChanged(card) {
      if (this.searchQuery.trim()) {
        const serviceCategory = this.getServiceTabCategory(card);
        if (serviceCategory) {
          this.currentTab = serviceCategory;
        }
      }
    },
    handleShowDetails(service) {
      this.openServiceModal(service);
    },
    openServiceModal(service) {
      $("#service_description_label").text(
        service.service_name || service.name
      );

      if (service.service_details && service.service_details.length > 0) {
        this.displayServiceDetails(service);
      } else {
        // This is a custom form - fetch its details from API
        this.displayCustomFormDetails(service.id);
      }

      $("#service_description_modal").modal("show");
    },
    displayServiceDetails(service) {
      const details = service.service_details[0];

      $("#service_description").text(
        details.service_description || "No description available"
      );
      $("#service_cost").text(details.service_cost || "Price not specified");
      $("#service_input").empty();
      $("#service_output").empty();

      try {
        const inputString = details.service_input.replace(/'/g, '"');
        const outputString = details.service_output.replace(/'/g, '"');
        const inputArray = JSON.parse(inputString);
        const outputArray = JSON.parse(outputString);

        inputArray.forEach((input) => {
          $("#service_input").append(`<li>${input}</li>`);
        });

        outputArray.forEach((output) => {
          $("#service_output").append(`<li>${output}</li>`);
        });
      } catch (error) {
        console.error("Error parsing service input/output", error);
      }
    },

    displayCustomFormDetails(serviceId) {
      const TOKEN = localStorage.getItem("token");
      const FORM_DETAILS_API = `${API_BASE_URL}/verify_api/service_management_api/get_custom_form_details/${serviceId}/`;

      // Show loading indicator
      $("#service_description").text("Loading form details...");
      $("#service_cost").text("");
      $("#service_input").empty();
      $("#service_output").empty();

      $.ajax({
        url: FORM_DETAILS_API,
        method: "GET",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          const questions = response.questions;

          // Display basic info
          $("#service_description").text("Custom Service");
          $("#service_cost").text("0.00");

          // Display questions as inputs
          $("#service_input").empty();
          questions.forEach((question) => {
            const questionText = question.question;
            $("#service_input").append(`<li>${questionText}</li>`);
          });

          // Output can show what the form collects
          $("#service_output").empty();
          $("#service_output").append("<li>Form submission data</li>");
        },
        error: (error) => {
          console.error("Error fetching form details", error);
          $("#service_description").text("Could not load form details");
          $("#service_input").empty();
          $("#service_output").empty();
        },
      });
    },

    removeService(service) {
      this.$emit("toggle-service", service, false);
    },

    navigateToService(service) {
      const serviceCategory = this.getServiceTabCategory(service);
      if (serviceCategory) {
        this.currentTab = serviceCategory;
      }
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (!this.$refs.magneticCarousel) return;

          const serviceIndex = this.filteredServices.findIndex(
            (s) => s.id === service.id
          );
          if (serviceIndex !== -1) {
            this.$refs.magneticCarousel.setCenterCard(serviceIndex);
          }
        });
      });
    },
    getServiceTabCategory(service) {
      if (this.personnelServices.find((s) => s.id === service.id))
        return "personnel";
      if (this.companyServices.find((s) => s.id === service.id))
        return "company";
      if (this.complianceServices.find((s) => s.id === service.id))
        return "compliance";
      if (this.creditServices.find((s) => s.id === service.id)) return "credit";
      if (this.customForms.find((s) => s.id === service.id)) return "custom";
      return null;
    },
    proceedToNext() {
      if (this.allSelectedServices.length === 0) {
        this.showWarning(
          "No Services Selected",
          "Please select at least one service to continue"
        );
        return;
      }

      this.$emit("continue", this.allSelectedServices);
    },
  },
};
</script>

<style scoped>
/* Search Bar */
.search-input-wrapper {
  position: relative;
  width: 300px;
  margin-top: 4px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  font-size: 0.875rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 8px 36px 8px 36px;
  border: 1px solid rgba(108, 117, 125, 0.25);
  border-radius: 20px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.search-input:focus {
  outline: none;
  background: white;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb, 89, 255, 116), 0.1);
}

.search-input::placeholder {
  color: #adb5bd;
  font-size: 0.875rem;
}

.clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-size: 0.75rem;
}

.clear-search:hover {
  background: rgba(108, 117, 125, 0.1);
  color: #495057;
}

/* No Results Message */
.no-results-message {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.no-results-message i {
  font-size: 3rem;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-results-message p {
  font-size: 1.1rem;
  margin: 0;
}

.carousel-section {
  margin: 40px 0;
}

.selected-services-container {
  margin-bottom: 10rem;
  padding-left: 0;
  padding-right: 0;
}

/* Selected Services Card */
.selected-services-card {
  background: rgba(248, 249, 250, 0.65);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(108, 117, 125, 0.2);
  border-radius: 24px;
  padding: 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-left: 0;
  margin-right: 0;
}

.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(108, 117, 125, 0.2);
}

.selected-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-title-section h5 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #212529;
  margin: 0;
}

.selected-title-section .badge {
  background: rgba(var(--primary-color-rgb, 89, 255, 116), 0.2);
  color: var(--primary-color);
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(var(--primary-color-rgb, 89, 255, 116), 0.3);
}

.selected-services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.selected-service-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(200, 200, 200, 0.15);
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.selected-service-chip:hover {
  background: rgba(73, 80, 87, 0.5);
  border-color: rgba(var(--primary-color-rgb, 89, 255, 116), 0.7);
  transform: translateY(-1px);
  box-shadow: 0 1px 2px rgba(var(--primary-color-rgb, 89, 255, 116), 0.15);
}

.chip-name {
  color: #212529;
  font-size: 0.9rem;
  font-weight: 500;
  flex: 1;
}

.chip-remove {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(108, 117, 125, 0.15);
  border: 1px solid rgba(108, 117, 125, 0.25);
  border-radius: 50%;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
}

.chip-remove:hover {
  background: rgba(108, 117, 125, 0.25);
  border-color: rgba(108, 117, 125, 0.4);
  color: #495057;
}

.selected-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid rgba(108, 117, 125, 0.2);
}

.btn-sm {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  border: 2px solid var(--primary-color);
  color: #212529;
}

.btn-primary:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(var(--primary-color-rgb, 89, 255, 116), 0.3);
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 30px;
  gap: 20px;
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
  border-width: 0.3rem;
  border-color: var(--primary-color);
  border-right-color: transparent;
}

.loading-text {
  font-size: 1rem;
  color: #6c757d;
  font-weight: 500;
  margin: 0;
}

/* Empty State */
.empty-state-card {
  background: rgba(248, 249, 250, 0.65);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(108, 117, 125, 0.2);
  border-radius: 24px;
  padding: 60px 30px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-icon {
  font-size: 3rem;
  color: rgba(108, 117, 125, 0.4);
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.empty-state-card h5 {
  font-size: 1.1rem;
  color: #212529;
  margin: 0 0 10px 0;
  font-weight: 600;
}

.empty-state-card p {
  font-size: 0.95rem;
  color: #6c757d;
  margin: 0;
}

/* Responsive */
@media (max-width: 992px) {
  .card-header.d-flex {
    flex-wrap: wrap;
    gap: 12px;
  }

  .search-input-wrapper {
    width: 250px;
  }
}

@media (max-width: 768px) {
  .search-input-wrapper {
    width: 200px;
  }

  .search-input {
    font-size: 0.8rem;
    padding: 6px 32px 6px 32px;
  }

  .search-icon {
    font-size: 0.75rem;
  }

  .selected-header {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .selected-services-grid {
    grid-template-columns: 1fr;
  }

  .selected-actions {
    flex-direction: column;
  }
}

@media (max-width: 576px) {
  .card-header.d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .search-input-wrapper {
    width: 100%;
    margin-top: 8px;
  }
}
</style>
